/**
 * ZicassoDashboard — Listado de leads (Trips) imitando el portal de Zicasso.
 * Filtros: búsqueda, países, asignado, orden, rangos de fecha, pills de Status
 * y Sales Stage, y toggle ALL / SOLD TRIPS. Cada fila abre el modal del lead.
 */
import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  RefreshCw, Search, Phone, MessageSquare, Bell, Mail, Compass, X,
} from "lucide-react";

import { api } from "@/lib/api";
import { formatTravelDates } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import ZicassoLeadModal from "./ZicassoLeadModal";

const COUNTRY_NAMES = {
  ES: "Spain", PT: "Portugal", IT: "Italy", FR: "France", GB: "United Kingdom",
  MA: "Morocco", GR: "Greece", DE: "Germany", NL: "Netherlands", CH: "Switzerland",
  AT: "Austria", BE: "Belgium", IE: "Ireland", HR: "Croatia", TR: "Turkey",
  US: "United States", MX: "Mexico", JP: "Japan", TH: "Thailand", IS: "Iceland",
};
const countryName = (code) => COUNTRY_NAMES[code] || code;
const countriesLabel = (arr = []) => {
  const names = arr.map(countryName);
  if (names.length <= 1) return names[0] || "—";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const STATUS_META = {
  ACTIVE: { label: "Active", cls: "bg-[#E2EBE5] text-[#244532]" },
  POSTPONED: { label: "Postponed", cls: "bg-[#F1E9DA] text-[#6B4F20]" },
  ACCEPTED: { label: "Accepted", cls: "bg-[#E5E8F2] text-[#34406B]" },
  SOLD: { label: "Sold", cls: "bg-[#E0EBEB] text-[#235858]" },
  INACTIVE: { label: "Inactive", cls: "bg-secondary text-muted-foreground" },
  TRAVELER_CANCELED: { label: "Canceled", cls: "bg-[#FCEEEA] text-[#803D26]" },
  SOLD_CANCELED: { label: "Canceled", cls: "bg-[#FCEEEA] text-[#803D26]" },
};

const STATUS_PILLS = [
  { label: "New", value: "NEW" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Active", value: "ACTIVE" },
  { label: "Sold", value: "SOLD" },
  { label: "Postponed", value: "POSTPONED" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Canceled", value: "CANCELED" },
];

const SALES_STAGE_PILLS = [
  { label: "First Message", values: ["FIRST_MESSAGE_SENT"] },
  { label: "Consultation", values: ["CONSULTATION"] },
  { label: "Proposal Sent", values: ["PROPOSAL_SENT"] },
  { label: "Itinerary Review", values: ["ITINERARY_IN_REVIEW", "ITINERARY_SENT"] },
];

const SORT_OPTIONS = [
  { value: "new_to_old", label: "New to Old" },
  { value: "old_to_new", label: "Old to New" },
  { value: "departure", label: "Departure (soonest)" },
  { value: "budget_high", label: "Budget (high to low)" },
  { value: "reminder", label: "Reminder (due first)" },
];

const STAGE_RANK = {
  "": 0, FIRST_MESSAGE_SENT: 1, CONSULTATION: 2,
  ITINERARY_IN_REVIEW: 3, ITINERARY_SENT: 4, PROPOSAL_SENT: 5,
};
const salesStageLabel = (s) => ({
  FIRST_MESSAGE_SENT: "First Message", CONSULTATION: "Consultation",
  ITINERARY_IN_REVIEW: "Itinerary Review", ITINERARY_SENT: "Itinerary Sent",
  PROPOSAL_SENT: "Proposal Sent",
}[s] || "New");

const fmtDate = (iso) => {
  if (!iso || iso.startsWith("0001")) return "—";
  try { return format(new Date(iso), "MMM dd, yyyy"); } catch { return "—"; }
};
const agentShort = (n = "") => {
  const p = n.trim().split(" ").filter(Boolean);
  if (!p.length) return "—";
  return p.length === 1 ? p[0] : `${p[0]} ${p[p.length - 1][0]}.`;
};

function StageBars({ stage }) {
  const rank = STAGE_RANK[stage || ""] ?? 0;
  const filled = Math.round((rank / 5) * 4);
  return (
    <span className="flex items-end gap-0.5" title={`Sales stage: ${salesStageLabel(stage)}`}>
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`w-1 rounded-sm ${i < filled ? "bg-[#347355]" : "bg-black/15"}`}
          style={{ height: `${7 + i * 3}px` }}
        />
      ))}
    </span>
  );
}

function LeadRow({ lead, onOpen }) {
  const status = STATUS_META[lead.status] || { label: lead.status || "—", cls: "bg-secondary text-muted-foreground" };
  const travelers = (lead.adults || 0) + (lead.children || 0);
  const prefix = (lead.traveler_phone_prefix || "").toString();
  const phone = lead.traveler_phone_number
    ? `${prefix.startsWith("+") ? prefix : prefix ? `+${prefix}` : ""} ${lead.traveler_phone_number}`.trim()
    : "";

  const copyPhone = async (e) => {
    e.stopPropagation();
    if (!phone) { toast.error("Sin teléfono"); return; }
    try {
      await navigator.clipboard?.writeText(phone);
      toast.success(`Teléfono copiado: ${phone}`);
    } catch {
      toast.info(phone);
    }
  };
  const openMessages = (e) => { e.stopPropagation(); onOpen(lead.id, "messages"); };

  return (
    <div
      onClick={() => onOpen(lead.id, "details")}
      className={`flex cursor-pointer items-center gap-4 rounded-xl border bg-card px-5 py-4 shadow-sm transition-all hover:shadow-md ${
        lead.is_overdue ? "border-[#C45A45]/30" : "border-black/5"
      }`}
      data-testid={`zicasso-lead-card-${lead.id}`}
    >
      {/* Nombre + países */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[15px] font-semibold text-foreground" data-testid={`lead-traveler-name-${lead.id}`}>
            {lead.traveler_name || "—"}
          </span>
          {lead.repeat_traveler && (
            <span className="rounded-full bg-[#E2EBE5] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#244532]">
              RPT
            </span>
          )}
          {lead.has_unread_from_traveler && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-[#C45A45]" title="Mensaje sin leer" />
          )}
        </div>
        <div className="mt-0.5 truncate text-[13px] text-muted-foreground">
          {countriesLabel(lead.countries)}
          {formatTravelDates(lead.departure_date, lead.return_date) && (
            <span className="text-foreground/70"> · {formatTravelDates(lead.departure_date, lead.return_date)}</span>
          )}
        </div>
      </div>

      {/* Salida + noches/viajeros */}
      <div className="hidden w-[150px] shrink-0 md:block">
        <div className="text-sm font-medium">{fmtDate(lead.departure_date)}</div>
        <div className="text-[12px] text-muted-foreground">
          {lead.total_nights || 0} Nights · {travelers} Traveler{travelers !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Presupuesto */}
      <div className="hidden w-[110px] shrink-0 md:block">
        <div className="text-sm font-semibold">
          ${(lead.budget_usd || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
        </div>
        <div className="text-[12px] text-muted-foreground">Total Budget</div>
      </div>

      {/* Asignado + matched */}
      <div className="hidden w-[150px] shrink-0 lg:block">
        <div className="text-sm font-medium">{agentShort(lead.assigned_zc_agent_name)}</div>
        <div className="text-[12px] text-muted-foreground">Matched: {fmtDate(lead.zc_created_at)}</div>
      </div>

      {/* Iconos */}
      <div className="hidden shrink-0 items-center gap-3 sm:flex">
        <button onClick={copyPhone} title={phone || "Sin teléfono"} className="text-foreground/80 transition-colors hover:text-primary" data-testid={`lead-phone-${lead.id}`}>
          <Phone className="h-4 w-4" />
        </button>
        <button onClick={openMessages} title="Ver conversación" className="relative text-foreground/40 transition-colors hover:text-primary" data-testid={`lead-message-${lead.id}`}>
          <MessageSquare className="h-4 w-4" />
          {lead.has_unread_from_traveler && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#C45A45]" />}
        </button>
        <StageBars stage={lead.sales_stage} />
      </div>

      {/* Estado */}
      <div className="w-[92px] shrink-0 text-right">
        <span className={`inline-block rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${status.cls}`} data-testid={`lead-status-${lead.id}`}>
          {status.label}
        </span>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
        active
          ? "border-transparent bg-[#1E2320] text-white"
          : "border-black/15 bg-transparent text-foreground hover:bg-black/5"
      }`}
    >
      {children}
    </button>
  );
}

export default function ZicassoDashboard() {
  const qc = useQueryClient();
  const [mode, setMode] = useState("all"); // all | sold
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("__all__");
  const [agent, setAgent] = useState("__all__");
  const [sort, setSort] = useState("new_to_old");
  const [matchStart, setMatchStart] = useState("");
  const [matchEnd, setMatchEnd] = useState("");
  const [depStart, setDepStart] = useState("");
  const [depEnd, setDepEnd] = useState("");
  const [statusSel, setStatusSel] = useState(new Set(["NEW", "ACCEPTED", "ACTIVE", "POSTPONED"]));
  const [stageSel, setStageSel] = useState(new Set());
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalTab, setModalTab] = useState("details");

  const stats = useQuery({ queryKey: ["zicasso-stats"], queryFn: async () => (await api.get("/zicasso/stats")).data, refetchInterval: 60_000 });
  const agents = useQuery({ queryKey: ["zicasso-agents"], queryFn: async () => (await api.get("/zicasso/agents")).data });
  const digest = useQuery({ queryKey: ["zicasso-digest"], queryFn: async () => (await api.get("/zicasso/daily-digest")).data });
  const countries = useQuery({ queryKey: ["zicasso-countries"], queryFn: async () => (await api.get("/zicasso/countries")).data });

  const params = useMemo(() => {
    const p = new URLSearchParams({ limit: "500", sort });
    if (mode === "sold") {
      p.set("statuses", "SOLD");
    } else {
      if (statusSel.size) p.set("statuses", [...statusSel].join(","));
      if (stageSel.size) {
        const vals = SALES_STAGE_PILLS.filter((s) => stageSel.has(s.label)).flatMap((s) => s.values);
        if (vals.length) p.set("sales_stages", vals.join(","));
      }
      if (unreadOnly) p.set("unread_only", "true");
      if (overdueOnly) p.set("overdue_only", "true");
    }
    if (country !== "__all__") p.set("countries", country);
    if (agent !== "__all__") p.set("assigned_zc_agent_id", agent);
    if (q) p.set("q", q);
    if (matchStart) p.set("match_start", matchStart);
    if (matchEnd) p.set("match_end", matchEnd);
    if (depStart) p.set("departure_start", depStart);
    if (depEnd) p.set("departure_end", depEnd);
    return p.toString();
  }, [mode, sort, statusSel, stageSel, country, agent, q, matchStart, matchEnd, depStart, depEnd, unreadOnly, overdueOnly]);

  const leads = useQuery({
    queryKey: ["zicasso-leads", params],
    queryFn: async () => (await api.get(`/zicasso/leads?${params}`)).data,
    keepPreviousData: true,
  });

  const syncMut = useMutation({
    mutationFn: () => api.post("/zicasso/sync?fetch_messages=true", {}),
    onSuccess: () => {
      toast.success("Sincronización arrancada en segundo plano");
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["zicasso-stats"] });
        qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
        qc.invalidateQueries({ queryKey: ["zicasso-digest"] });
        qc.invalidateQueries({ queryKey: ["zicasso-countries"] });
      }, 8000);
    },
    onError: () => toast.error("Error al arrancar el sync"),
  });

  const toggleStatus = (v) => setStatusSel((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });
  const toggleStage = (label) => setStageSel((prev) => { const n = new Set(prev); n.has(label) ? n.delete(label) : n.add(label); return n; });
  const openLead = (id, tab = "details") => { setModalTab(tab); setSelectedLead(id); };

  const clearFilters = () => {
    setQ(""); setCountry("__all__"); setAgent("__all__"); setSort("new_to_old");
    setMatchStart(""); setMatchEnd(""); setDepStart(""); setDepEnd("");
    setStatusSel(new Set(["NEW", "ACCEPTED", "ACTIVE", "POSTPONED"])); setStageSel(new Set());
    setUnreadOnly(false); setOverdueOnly(false);
  };

  const items = leads.data?.items || [];
  const total = leads.data?.total || 0;
  const digestTotal = digest.data?.total || 0;
  const unreadTotal = stats.data?.totals?.unread_from_traveler ?? 0;

  return (
    <div className="mx-auto max-w-[1500px] px-6 py-6" data-testid="zicasso-dashboard">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Leads</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {stats.data?.last_sync
              ? `Última sync: ${format(new Date(stats.data.last_sync.ts), "d MMM · HH:mm", { locale: es })}`
              : "Aún sin sincronizar con Zicasso Partners"}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-sm font-semibold">
            <button
              onClick={() => setMode("all")}
              className={`px-2 py-1 uppercase tracking-wide transition-colors ${mode === "all" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-all-trips"
            >
              All Trips
            </button>
            <span className="text-muted-foreground/40">·</span>
            <button
              onClick={() => setMode("sold")}
              className={`px-2 py-1 uppercase tracking-wide transition-colors ${mode === "sold" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="tab-sold-trips"
            >
              Sold Trips
            </button>
          </div>
          <Button onClick={() => syncMut.mutate()} disabled={syncMut.isPending} data-testid="sync-now-button">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncMut.isPending ? "animate-spin" : ""}`} />
            Sincronizar ahora
          </Button>
        </div>
      </div>

      {/* Indicadores: atención hoy + sin leer */}
      {mode === "all" && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setOverdueOnly((v) => !v)}
            className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${overdueOnly ? "border-[#C45A45] bg-[#FCEEEA] text-[#803D26]" : "border-black/10 bg-card text-foreground hover:bg-black/5"}`}
            data-testid="chip-attention-today"
          >
            <Bell className="h-3.5 w-3.5 text-[#C45A45]" />
            {digestTotal} necesitan atención hoy
          </button>
          <button
            onClick={() => setUnreadOnly((v) => !v)}
            className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${unreadOnly ? "border-[#C28F5B] bg-[#F1E9DA] text-[#6B4F20]" : "border-black/10 bg-card text-foreground hover:bg-black/5"}`}
            data-testid="chip-unread"
          >
            <Mail className="h-3.5 w-3.5 text-[#C28F5B]" />
            {unreadTotal} sin leer
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-5 space-y-4 rounded-xl border border-black/5 bg-card p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <FilterField label="Search">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar cliente, título, ref…" className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="zicasso-filter-search" />
            </div>
          </FilterField>
          <FilterField label="Countries">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="zicasso-filter-country"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All countries</SelectItem>
                {(countries.data?.countries || []).map((c) => (
                  <SelectItem key={c} value={c}>{countryName(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Assignee">
            <Select value={agent} onValueChange={setAgent}>
              <SelectTrigger data-testid="zicasso-filter-agent"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {(agents.data || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Sort By">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="zicasso-filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <FilterField label="Match Start">
            <Input type="date" value={matchStart} onChange={(e) => setMatchStart(e.target.value)} data-testid="filter-match-start" />
          </FilterField>
          <FilterField label="Match End">
            <Input type="date" value={matchEnd} onChange={(e) => setMatchEnd(e.target.value)} data-testid="filter-match-end" />
          </FilterField>
          <FilterField label="Departure Start">
            <Input type="date" value={depStart} onChange={(e) => setDepStart(e.target.value)} data-testid="filter-departure-start" />
          </FilterField>
          <FilterField label="Departure End">
            <Input type="date" value={depEnd} onChange={(e) => setDepEnd(e.target.value)} data-testid="filter-departure-end" />
          </FilterField>
        </div>

        {mode === "all" && (
          <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
              <div className="flex flex-wrap gap-2" data-testid="status-pills">
                {STATUS_PILLS.map((p) => (
                  <Pill key={p.value} active={statusSel.has(p.value)} onClick={() => toggleStatus(p.value)} testid={`status-pill-${p.value}`}>
                    {p.label}
                  </Pill>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sales Stage</div>
              <div className="flex flex-wrap gap-2" data-testid="stage-pills">
                {SALES_STAGE_PILLS.map((p) => (
                  <Pill key={p.label} active={stageSel.has(p.label)} onClick={() => toggleStage(p.label)} testid={`stage-pill-${p.label.replace(/\s/g, "-")}`}>
                    {p.label}
                  </Pill>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resultados */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold" data-testid="results-count">
          {leads.isLoading ? "…" : `${total} Result${total !== 1 ? "s" : ""}`}
        </h2>
        <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="clear-filters-button">
          <X className="h-3 w-3" /> Limpiar filtros
        </button>
      </div>

      {leads.isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-[76px] w-full rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-16 text-center text-muted-foreground" data-testid="zicasso-empty-state">
          <Compass className="mx-auto mb-3 h-8 w-8 opacity-40" />
          <p>Sin resultados con estos filtros.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="zicasso-leads-list">
          {items.map((lead) => <LeadRow key={lead.id} lead={lead} onOpen={openLead} />)}
        </div>
      )}

      <ZicassoLeadModal leadId={selectedLead} open={!!selectedLead} onClose={() => setSelectedLead(null)} initialTab={modalTab} />
    </div>
  );
}
